"use client";
import { useUserContext } from "@/context/UserProvider";
import { useClientTranslations } from "@/hooks/useClientTranslations";
import { useDialect } from "@/hooks/useDialect";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Box } from "./Box";
import {
  HomeFilled,
  HomeNoFill,
  MyListFilled,
  MyListNoFill,
  PlusIcon,
  SearchIcon,
  SettingsIcon,
  ProfileFilled,
  ProfileNoFill,
  SearchFilled,
  SearchNoFill,
  SettingsFilled,
  SettingsNoFill,
} from "./Icons";
import { Column, Row } from "./Stack";
import { Text } from "./Text";
import { LoginDrawer } from "./login/LoginDrawer";
import { Button } from "./Button";
import { SourcePagesEnum, sourcePagesValues } from "@/constants/constants";

export const BottomNavigation = () => {
  const { t } = useClientTranslations();
  const { isLoggedIn } = useUserContext();
  const { currentDialect } = useDialect();
  const { currentLanguage } = useClientTranslations();
  const pathname = usePathname();

  const basePath = `/${currentLanguage}/${currentDialect.dialect.toLowerCase()}`;

  const routes = [
    {
      name: t("common.home"),
      unSelectedIcon: HomeNoFill,
      selectedIcon: HomeFilled,
      path: `${basePath}`,
    },
    {
      name: t("common.search"),
      unSelectedIcon: SearchNoFill,
      selectedIcon: SearchFilled,
      path: isLoggedIn ? `${basePath}/search` : `${basePath}/login`,
      loginSource: sourcePagesValues[SourcePagesEnum.SEARCH],
    },
    {
      name: t("common.myList"),
      unSelectedIcon: MyListNoFill,
      selectedIcon: MyListFilled,
      path: `${basePath}/my-list`,
    },
    isLoggedIn
      ? {
          name: t("setting_page.title"),
          unSelectedIcon: SettingsNoFill,
          selectedIcon: SettingsFilled,
          path: `${basePath}/settings`,
        }
      : {
          name: t("auth_screen.login"),
          unSelectedIcon: ProfileNoFill,
          selectedIcon: ProfileFilled,
          path: `${basePath}/login`,
        },
  ];

  return (
    <nav
      className="mobile-bottom-navigation-shadow fixed bottom-0 left-0 right-0 z-[1] border-t border-white/10 bg-stg-mobile-bottom-navigation-background"
      data-testid="bottom-navigation"
    >
      <Box paddingVertical={4} paddingHorizontal={2} width="w-full">
        <Row fullWidth justify="spaceAround">
          {routes.map((route) => {
            const isLoginCTA = route.path.includes("/login");
            if (isLoginCTA) {
              return (
                <LoginDrawer
                  key={route.name}
                  isLoading={false}
                  label={route.name}
                  loginSource={
                    route?.loginSource ??
                    sourcePagesValues[SourcePagesEnum.HOME]
                  }
                  triggerComponent={
                    <Button noBg>
                      <BottomNavigationItem route={route} pathname={pathname} />
                    </Button>
                  }
                />
              );
            }
            return (
              <Link
                href={route.path}
                key={route.name}
                className={` ${pathname === route.path ? "text-white" : ""}`}
              >
                <BottomNavigationItem route={route} pathname={pathname} />
              </Link>
            );
          })}
        </Row>
      </Box>
    </nav>
  );
};

const BottomNavigationItem = ({
  route,
  pathname,
}: {
  route: {
    name: string;
    unSelectedIcon: React.ComponentType<{ height: number; width: number }>;
    selectedIcon: React.ComponentType<{ height: number; width: number }>;
    path: string;
  };
  pathname: string;
}) => {
  return (
    <div
      key={route.name}
      className="flex cursor-pointer flex-col items-center gap-2"
    >
      {pathname === route.path ? (
        <route.selectedIcon height={20} width={20} />
      ) : (
        <route.unSelectedIcon height={20} width={20} />
      )}
      <Text
        size={2}
        appearance={pathname === route.path ? "primary" : "secondary"}
        weight="medium"
      >
        {route.name}
      </Text>
    </div>
  );
};
